import type { AxiosRequestConfig } from "axios";
import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  IntakeQuestionnaire,
  IntakeQuestionnaireBranding,
  IntakeQuestionnaireInvitation,
  Response,
  SubmitAssessmentAnswersPayload,
} from "~/types/api";

/** Proves the date of birth was confirmed; the backend reads it on every request behind the link. */
export const INTAKE_QUESTIONNAIRE_ACCESS_HEADER = "X-Intake-Questionnaire-Access";

/**
 * The public link from the mail before an intake. No login and no location context: the token in
 * the path opens it, the access grant from the date-of-birth check unlocks it. A 401 here means
 * "confirm the date of birth again", never "refresh the login", so the auth retry stays off.
 */
export function createIntakeQuestionnaireApi({ getRequest, postRequest, putRequest }: ApiRequestHelpers) {
  const publicRequest = (accessGrant?: string): AxiosRequestConfig => ({
    omitContextHeaders: true,
    disableAuthRetry: true,
    suppressValidationToast: true,
    headers: accessGrant ? { [INTAKE_QUESTIONNAIRE_ACCESS_HEADER]: accessGrant } : {},
  });

  const intakeQuestionnaireBranding = (token: string) =>
    getRequest<Response<IntakeQuestionnaireBranding>>(
      `/intake-questionnaires/${encodeURIComponent(token)}/branding`,
      publicRequest(),
    );

  const verifyIntakeQuestionnaireAccess = (token: string, birthDate: string) =>
    postRequest<Response<{ access_token: string }>>(
      `/intake-questionnaires/${encodeURIComponent(token)}/verify`,
      { birth_date: birthDate },
      publicRequest(),
    );

  const intakeQuestionnaireInvitation = (token: string, accessGrant: string) =>
    getRequest<Response<IntakeQuestionnaireInvitation>>(
      `/intake-questionnaires/${encodeURIComponent(token)}`,
      publicRequest(accessGrant),
    );

  const intakeQuestionnaire = (token: string, accessGrant: string, questionnaireId: string) =>
    getRequest<Response<IntakeQuestionnaire>>(
      `/intake-questionnaires/${encodeURIComponent(token)}/questionnaires/${questionnaireId}`,
      publicRequest(accessGrant),
    );

  const saveIntakeQuestionnaireAnswers = (
    token: string,
    accessGrant: string,
    questionnaireId: string,
    payload: SubmitAssessmentAnswersPayload,
  ) =>
    putRequest<Response<IntakeQuestionnaire>>(
      `/intake-questionnaires/${encodeURIComponent(token)}/questionnaires/${questionnaireId}/answers`,
      payload,
      publicRequest(accessGrant),
    );

  return {
    intakeQuestionnaireBranding,
    verifyIntakeQuestionnaireAccess,
    intakeQuestionnaireInvitation,
    intakeQuestionnaire,
    saveIntakeQuestionnaireAnswers,
  };
}

export type IntakeQuestionnaireApi = ReturnType<typeof createIntakeQuestionnaireApi>;
